<template>
    <div>
        <transition name="dialogfade">
            <k-dialog v-if="showDialog"
                class="edit-condition-dialog"
                :title="condition.title"
                @close="closeDialog">
                <div class="edit-condition__wrapper">
                    <div class="edit-condition__detail-tracking">
                        <section class="edit-condition__section-detail">
                            <h2 class="section--title">Condition Detail</h2>
                            <div class="added__info">
                                <label>
                                    Added Date
                                    <k-input disabled :value="condition.createdDate.toLocaleDateString()" />
                                </label>
                                <label>
                                    Added By
                                    <k-input disabled :value="condition.addedBy" />
                                </label>
                            </div>

                            <label>
                                Prior To
                                <k-dropdown @change="checkForConditionChanges" :data-items="priorToSelects" v-model="editedCondition.priorTo"></k-dropdown>
                            </label>
                            <label>
                                <span class="external__label-row">Condition Description <k-button v-on:click="populateInternal" :disabled="!editedCondition.externalDescription || editedCondition.externalDescription === editedCondition.internalDescription" class="copy-external__btn">Copy to UW Notes</k-button></span>
                                <k-textarea @input="checkForConditionChanges" :rows="5" v-model="editedCondition.externalDescription"></k-textarea>
                            </label>
                            <label>
                                <span class="external__label-row">Internal UW Notes <k-button v-on:click="populateExternal" :disabled="!editedCondition.internalDescription || editedCondition.internalDescription === editedCondition.externalDescription" class="copy-external__btn">Copy to Description</k-button></span>
                                <k-textarea @input="checkForConditionChanges" :rows="5" v-model="editedCondition.internalDescription"></k-textarea>
                            </label>
                            <label class="print-def-label">Print Definitions</label>
                            <div class="added__info print-def">
                                <k-checkbox class="print-def-checkbox" :checked="conditionPrintDefinitions.find(def => def.type === 'ExternalPrint')?.checked" @change="onPrintDefChange($event, 'ExternalPrint')">
                                    <label>Print on Approval Letter/Show in TPO Connect</label>
                                </k-checkbox>
                                <k-checkbox class="print-def-checkbox" :checked="conditionPrintDefinitions.find(def => def.type === 'InternalPrint')?.checked" @change="onPrintDefChange($event, 'InternalPrint')">
                                    <label>UW Department Only</label>
                                </k-checkbox>
                            </div>
                        </section>
                        <section class="edit-condition__section-tracking">
                            <h2 class="section--title">Tracking Status</h2>
                            <div v-for="status in trackingStatus" :key="status.status" v-bind:class="['tracking-checkbox__container']">
                                <k-checkbox @change="checkForConditionChanges" v-model="status.isChecked" :id="status.status">
                                    <label :for="status.status" class="tracking-status__label">{{ status.status }} <span class="track--detail" v-if="status.user">{{ status.statusDate }} by {{ formatCommaName(status.user) }}</span></label>
                                </k-checkbox>
                            </div>
                        </section>
                    </div>
                    <div class="edit-condition__attachments">
                        <div class="attachments-grid__panel-container">
                            <k-panelbar :expand-mode="'single'" :items="panelItems" class="attachments-grid__panel">
                                <template v-slot:attachmentsgrid>
                                    <div class="edit-condition__assign-doc">
                                        <span class="document--info"><span class="k-icon k-i-information k-i-info"></span> Select a Document To View</span>
                                        <k-fade :appear="saveChangesWarning">
                                            <p class="save-changes-warning" v-if="saveChangesWarning"><span class="k-icon k-i-warning k-i-exception k-i-error"></span>Please save your changes before continuing</p>
                                        </k-fade>
                                        <k-button @click="onAssignDocument" class="assign-doc__btn"><span class="k-icon k-i-file .k-i-file-vertical"></span> Assign Document</k-button>
                                        <k-button v-if="conditionAttachments?.length > 0" @click="onViewAllFiles" class="assign-doc__btn view-all"><span class="k-icon k-i-file .k-i-file-vertical"></span> View All</k-button>

                                    </div>
                                    <k-grid :data-items="conditionAttachments"
                                        :columns="attachmentColumns"
                                        :sortable="sortable"
                                        :sort="attachmentSort"
                                        :selected-field="selectedField"
                                        @sortchange="attachmentSortChangeHandler"
                                        @rowclick="onOpenAttachment">
                                        <template v-slot:versionCell="{props}">
                                            <td class="attachment__version-cell">
                                                <span v-if="props.dataItem.isActive" class="k-icon k-i-check-circle active"></span>
                                                <span v-else class="k-icon k-i-minus not-active"></span>
                                            </td>
                                        </template>
                                    </k-grid>
                                </template>
                            </k-panelbar>
                        </div>
                        <div class="viewer__container">
                          <WebViewer @removeAttchment="RemoveAttachments"  :showEditorButton="false" :selectedAttchement="selectedAttachment" :pdfFile="selectedAttachment.url" v-if="!isAttachmentLoading && selectedAttachment.url" />
                          <div class="document-attachment__loader-container" v-if="isAttachmentLoading">
                            <loader :size="'large'" :type="'pulsing'"></loader>
                          </div>
                        </div>
                    </div>
                </div>
                <k-dialog-actions>
                    <k-button v-bind:class="['dialog__btn dialog__btn--save', { 'dialog__btn--save-active': !saveConditionBtnDisabled }]" v-on:click="saveCondition">{{ saveConditionBtnDisabled ? 'Close' : 'Save and Close' }}</k-button>
                </k-dialog-actions>

                <div class="conditions__loader-container" v-if="isLoading">
                    <loader :size="'large'" :type="'infinite-spinner'"></loader>
                </div>
            </k-dialog>
        </transition>

        <transition name="dialogfade">
            <k-dialog v-if="showDocumentsDialog"
                :title="'Assign Documents'"
                class="edit-condition-dialog assign-documents-dialog"
                @close="toggleAssignDocumentDialog">
                <section class="add-document__section-detail detail--current-documents">
                    <div class="select-document__wrapper">
                        <h2 class="section--title">Current Documents</h2>
                            <div class="condition-document__container">
                                <div class="condition-document__row" v-for="document in currentAssignedDocuments" :key="document.id">
                                    <p class="condition-document__item">
                                        <span class="condition-document__item--name">{{ document.title }}</span>
                                    </p>
                                </div>
                            </div>
                    </div>
                </section>
                <section class="add-document__section-detail detail--add-documents">
                    <div class="select-document__wrapper">
                        <h2 class="section--title">Add Document(s)</h2>
                        <div class="select-document__dropdown">
                            <k-multiselect :data-items="filteredAvailableDocuments"         
                                :text-field="'title'"
                                :data-item-key="'id'"
                                :placeholder="'Select Document'"
                                v-model="selectedDocuments"
                                :filterable="true"
                                @filterchange="onDocumentsFilterChange"></k-multiselect>
                        </div>
                    </div>
                </section>
                <k-dialog-actions>
                    <k-button class="dialog__btn dialog__btn--save" v-on:click="assignDocuments" :disabled="assignDocumentsBtnDisabled">Save</k-button>
                    <k-button class="dialog__btn dialog__btn--close" v-on:click="cancelAssignDocumentDialog">Close</k-button>
                </k-dialog-actions>
            </k-dialog>
        </transition>

        <transition name="dialogfade">
            <k-dialog v-if="showConfirmCloseDialog"
            class="edit-condition-dialog confirm-junk-dialog"
            :title="'You have unsaved changes'"
            @close="toggleConfirmCloseDialog">
                <p class="confirm-junk--msg">Are you sure you would like to cancel your changes?</p>
                <k-dialog-actions>
                    <k-button class="dialog__btn dialog__btn--close" v-on:click="toggleConfirmCloseDialog">No</k-button>
                    <k-button class="dialog__btn dialog__btn--save" v-on:click="confirmCloseDialog">Yes</k-button>
                </k-dialog-actions>
            </k-dialog>
        </transition>

        <k-notification-group class="conditions__notification-container">
            <k-fade :appear="successNotification">
                <k-notification v-if="successNotification"
                                :type="{ style: 'success', icon: true }"
                                :closable="true"
                                @close="onCloseNotification('successNotification')">
                    <span>{{ notificationMsg }}</span>
                </k-notification>
            </k-fade>
        </k-notification-group>

        <transition name="dialogfade">
          <k-dialog v-if="showErrorDialog" :title="errorTitle" class="error__dialog" @close="toggleErrorDialog">
            <p class="error-dialog__msg">{{ errorMsg }}</p>
          </k-dialog>
        </transition>
    </div>
</template>

<script>
    import auth from '../authConfig.js';
    import apiService from '../api/api.service.js';

    import { Dialog, DialogActionsBar, Window } from '@progress/kendo-vue-dialogs';
    import { Input, TextArea, Checkbox } from "@progress/kendo-vue-inputs";
    import { Button } from '@progress/kendo-vue-buttons';
    import { MultiSelect, DropDownList } from '@progress/kendo-vue-dropdowns';
    import { Loader } from "@progress/kendo-vue-indicators";
    import { Grid, GridToolbar } from '@progress/kendo-vue-grid';
    import { Notification, NotificationGroup } from "@progress/kendo-vue-notification";
    import { Fade, Reveal, Slide } from "@progress/kendo-vue-animation";
    import { PanelBar } from '@progress/kendo-vue-layout';

    import { filterBy } from '@progress/kendo-data-query';
    import { orderBy } from '@progress/kendo-data-query';

    import isEqual from 'lodash.isequal';
    import cloneDeep from 'lodash.clonedeep';
    import WebViewer from "../components/WebViewer.vue";

    export default {
        props: ['allDocuments', 'condition', 'loanId', 'showDialog'],
        emits: ['closenotification', 'conditionupdated', 'dialogclosed', 'requesterror', 'shownotification', 'updatecondition', 'updatetracking'],
        data() {
            return {
                assignDocumentsBtnDisabled: true,
                availableDocuments: [],
                attachmentColumns: [
                    { field: 'title', title: 'Title' },
                    { field: 'createdBy.entityId', title: 'Added By' },
                    { field: 'createdDate', title: 'Date Added', filter: "date", type: "date", format: "{0:MM/dd/yyyy}" },
                    { cell: 'versionCell', field: 'isActive', title: 'Current Version', width: '150px', columnMenu: false, filterable: false },
                ],
                attachmentSort: [],
                conditionAttachments: [],
                conditionPrintDefinitions: [
                    { type: 'InternalPrint', checked: false },
                    { type: 'ExternalPrint', checked: false }
                ],
                conditionPrintDefinitionsModel: [
                    { type: 'InternalPrint', checked: false },
                    { type: 'ExternalPrint', checked: false }
                ],
                conditionUpdated: false,
                currentAssignedDocuments: [],
                editedCondition: {
                    id: '',
                    internalDescription: '',
                    externalDescription: '',
                    priorTo: '',
                    title: ''
                },
                errorAlert: false,
                errorMsg: '',
                errorTitle: '',
                filteredAvailableDocuments: [],
                isAttachmentLoading: false,
                isLoading: false,
                notificationMsg: '',
                panelItems: [
                    {
                        title: 'Supporting Documents',
                        content: 'attachmentsgrid',
                        expanded: false
                    }
                ],
                priorToSelects: ['Approval', 'Closing', 'Docs', 'Purchase'],
                selectedField: 'selected',
                saveChangesWarning: false,
                saveConditionBtnDisabled: true,
                selectedAttachment: { url: '', id: '', title: '' },
                selectedDocuments: [],
                selectedId: null,
                showAttachment: false,
                showConfirmCloseDialog: false,
                showDocumentsDialog: false,
                showErrorDialog: false,
                sortable: { allowUnsort: false, mode: 'single' },
                successNotification: false,
                trackingStatus: [],
                trackingStatusModel: [
                    {
                        status: 'Requested',
                        isChecked: false,
                        user: '',
                        statusDate: ''
                    },
                    {
                        status: 'Re-requested',
                        isChecked: false,
                        user: '',
                        statusDate: ''
                    },
                    {
                        status: 'Fulfilled',
                        isChecked: false,
                        user: '',
                        statusDate: ''
                    },
                    {
                        status: 'Reviewed',
                        isChecked: false,
                        user: '',
                        statusDate: ''
                    },
                    {
                        status: 'Rejected',
                        isChecked: false,
                        user: '',
                        statusDate: ''
                    },
                    {
                        status: 'Cleared',
                        isChecked: false,
                        user: '',
                        statusDate: ''
                    },
                    {
                        status: 'Waived',
                        isChecked: false,
                        user: '',
                        statusDate: ''
                    },
                    {
                        status: 'Removed By Swift',
                        isChecked: false,
                        user: '',
                        statusDate: ''
                    }
                ],
                trackingUpdated: false,
                uneditedCondition: {},
                undeditedTrackingStatus: []
            }
        },
        computed: {

        },
        mounted() {

        },
        watch: {
            showDialog(val) {
                if (val) {
                    this.processSelectedCondition();
                } else {
                    this.trackingStatus = [];
                    this.conditionUpdated = false;
                    this.trackingUpdated = false;
                    this.saveConditionBtnDisabled = true;
                    this.showAttachment = false;
                    this.selectedAttachment = { url: '', id: '', title: '' };
                    this.availableDocuments = [];
                    this.currentAssignedDocuments = [];

                    this.uneditedCondition = {},
                    this.editedCondition = {
                        id: '',
                        internalDescription: '',
                        externalDescription: '',
                        priorTo: '', 
                        title: ''
                    }

                    this.isLoading = false;
                    this.isAttachmentLoading = false;
                    this.saveChangesWarning = false;
                    this.selectedId = null;
                    this.conditionPrintDefinitions = cloneDeep(this.conditionPrintDefinitionsModel);
                }
            },
            selectedDocuments(val) {
                if (val.length > 0) {
                    return this.assignDocumentsBtnDisabled = false;
                }

                this.assignDocumentsBtnDisabled = true;
            },
            condition(val) {
                this.isLoading = false;

                this.processSelectedCondition();
            }
        },
    methods: {
        async RemoveAttachments(id) {
            let tokenResponse = await auth.acquireTokenResponse();
            this.idTokenClaims = tokenResponse.idTokenClaims;

            let removedAttachment = {
            entityId: id,
            entityType: 'Attachment'
            }

            apiService.MoveAttachmentToJunkFolder(this.loanId, this.junkFolderId, removedAttachment, tokenResponse.accessToken)
            .then(res => {
                this.$emit('documentupdated', removedAttachment.entityId);


            }).catch(error => {
                this.errorTitle = 'Failed to Remove Attachment';

                if (error?.response?.data?.data) {
                    this.errorMsg = "Error: \n\n" + error.response.data.data;
                } else {
                    this.errorMsg = '';
                }

                this.onRequestError(this.errorTitle, this.errorMsg);
            });
        },
        async assignDocuments() {
            this.$emit('shownotification', 'Assigning document...', true);
            this.toggleAssignDocumentDialog();

            this.isLoading = true;

            let tokenResponse = await auth.acquireTokenResponse();
            this.idTokenClaims = tokenResponse.idTokenClaims;

            let addedDocuments = [{
                conditionId: this.condition.id,
                documents: this.selectedDocuments.map(doc => {
                    return {
                        entityId: doc.id,
                        entityType: 'Document'
                    }
                })
            }]

            apiService.AttachDocumentConditions(this.loanId, addedDocuments, tokenResponse.accessToken)
                .then(res => {
                    this.$emit('conditionupdated', addedDocuments[0].conditionId);
                    this.selectedDocuments = [];

                    this.$emit('closenotification', 'progressNotification');
                    this.$emit('shownotification', 'Document(s) assigned successfully');
                }).catch(error => {
                    this.$emit('closenotification', 'progressNotification');
                    this.errorTitle = 'Failed to Assign Document(s)';

                    if(error?.response?.data?.data) {
                        this.errorMsg = "Error: \n\n" + error.response.data.data;
                    } else {
                        this.errorMsg = '';
                    }

                    this.onRequestError(this.errorTitle, this.errorMsg);
                });               
            },
            assignSelectedDocument() {
                if (this.selectedId === 'All') {
                    this.selectedAttachment.id = null;
                    return this.onViewAllFiles();
                }

                if (this.conditionAttachments.some(att => this.selectedId === att.id)) {
                    return this.conditionAttachments.find(att => this.selectedId === att.id).selected = true;
                }

                this.onOpenAttachment({ dataItem: { id: this.conditionAttachments[0].id, title: this.conditionAttachments[0].title, isActive: this.conditionAttachments[0].isActive }});
            },
            attachmentSortChangeHandler(evt) {
                this.attachmentSort = evt.sort;

                this.conditionAttachments = orderBy(this.conditionAttachments, this.attachmentSort);
            },
            base64ToBlob(base64, type='application/octet-stream') {
                const binStr = atob(base64);
                const len = binStr.length;
                const arr = new Uint8Array(len);

                for (let i = 0; i< len; i++) {
                    arr[i] = binStr.charCodeAt(i);
                }

                return new Blob([arr], {type: type});
            },
            cancelAssignDocumentDialog() {
                this.toggleAssignDocumentDialog();
                this.selectedDocuments = [];
            },
            checkForConditionChanges() {
                if (!isEqual(this.editedCondition, this.uneditedCondition)) {
                    this.conditionUpdated = true;
                } else {
                    this.conditionUpdated = false;
                }

                if (!isEqual(this.trackingStatus, this.undeditedTrackingStatus)) {
                    this.trackingUpdated = true;
                } else {
                    this.trackingUpdated = false;
                }

                if (this.conditionUpdated || this.trackingUpdated) {
                    return this.saveConditionBtnDisabled = false;
                }

                this.saveChangesWarning = false;
                this.saveConditionBtnDisabled = true;
            },
            closeDialog() {
                if (!this.saveConditionBtnDisabled) return this.toggleConfirmCloseDialog();
                this.$emit('dialogclosed');
            },
            confirmCloseDialog() {
                this.showConfirmCloseDialog = !this.showConfirmCloseDialog;

                this.$emit('dialogclosed');
            },
            formatCommaName(val) {
                if (val) {
                    return val.split(', ').reverse().join(' ');
                }

                return val;
            },
            getUpdatedTracking() {
                let tracking = cloneDeep(this.trackingStatus);

                this.condition.tracking.forEach(track => {
                    let uneditedTrackIndex = tracking.findIndex(t => t.status === track.status);

                    if (uneditedTrackIndex >= 0) {
                        if (track.isChecked === tracking[uneditedTrackIndex].isChecked) {
                            tracking.splice(uneditedTrackIndex, 1);
                        }
                    }
                });

                return tracking;
            },
            onAssignDocument() {
                if (!this.saveConditionBtnDisabled) {
                    return this.saveChangesWarning = true;
                }

                this.toggleAssignDocumentDialog();
            },
            onCloseNotification(flag) {
                this[flag] = false;
            },
            onDocumentsFilterChange(evt) {
                this.filteredAvailableDocuments = filterBy(this.availableDocuments, evt.filter);
            },
            async onOpenAttachment(evt) {
                if (this.selectedAttachment.id === evt.dataItem.id) return;

                this.selectedId = evt.dataItem.id;
                
                this.conditionAttachments.forEach(att => {
                  if (att.id === this.selectedId) {
                    att.selected = true;
                  } else {
                    att.selected = false;
                  }
                });

                this.showAttachment = false;

                this.selectedAttachment.url = '';
                this.selectedAttachment.id = evt.dataItem.id;
                this.selectedAttachment.title = evt.dataItem.title;
                
                this.isAttachmentLoading = true;

                let tokenResponse = await auth.acquireTokenResponse();
                this.idTokenClaims = tokenResponse.idTokenClaims;

                let attachmentsFile = {
                    loanId: this.loanId,
                    attachments: [evt.dataItem.id]
                };

                apiService.GetAttachmentsFileBytes(attachmentsFile, tokenResponse.accessToken, evt.dataItem.isActive ? "false" : "true" )
                    .then(res => {
                        const blob = this.base64ToBlob(res.data.data, 'application/pdf');
                        const url = URL.createObjectURL(blob);

                        this.isAttachmentLoading = false;
                        this.selectedAttachment.url = url;
                        this.showAttachment = true;
                    }).catch(error => {
                        this.errorTitle = 'Failed to Open Attachment';

                        if(error.response.data.data) {
                          this.errorMsg = "Error: \n\n" + error.response.data.data;
                        } else {
                            this.errorMsg = '';
                        }

                        this.onRequestError(this.errorTitle, this.errorMsg);
                    });
            },
            onPrintDefChange(evt, defType) {
                let definition = this.conditionPrintDefinitions.find(def => def.type === defType);

                if (definition) {
                    definition.checked = evt.value;
                    let definitions = this.conditionPrintDefinitions.filter(def => def.checked).map(def => def.type);

                    this.editedCondition.printDefinitions = definitions.sort();
                    this.checkForConditionChanges();
                }
            },
            onRequestError(errorTitle, errorMsg) {
                this.isLoading = false;
                this.isAttachmentLoading = false;
                this.errorAlert = true;

                this.errorTitle = errorTitle;

                if (errorMsg) {
                    this.errorMsg = errorMsg;
                } else {
                    this.errorMsg = 'Please try again';
                }

                this.toggleErrorDialog();
            },
            async onViewAllFiles() {
                if (this.conditionAttachments?.length === 0 || this.selectedAttachment.id === 'All') return;

                this.selectedId = 'All';

                this.conditionAttachments.forEach(att => {
                    if (att.isActive) {
                        att.selected = true;
                    } else {
                        att.selected = false;
                    }
                });

                let attachments = this.conditionAttachments.map(att => att.id);

                this.showAttachment = false;

                this.selectedAttachment.url = '';
                this.selectedAttachment.id = 'All';
                this.selectedAttachment.title = this.condition.title;

                this.isAttachmentLoading = true;

                let tokenResponse = await auth.acquireTokenResponse();
                this.idTokenClaims = tokenResponse.idTokenClaims;

                let attachmentsFile = {
                    loanId: this.loanId,
                    attachments: attachments
                };

                apiService.GetMergedAttachmentsFileBytes(attachmentsFile, tokenResponse.accessToken)
                    .then(res => {
                        const blob = this.base64ToBlob(res.data.data, 'application/pdf');
                        const url = URL.createObjectURL(blob);

                        this.isAttachmentLoading = false;
                        this.selectedAttachment.url = url;
                        this.showAttachment = true;
                    }).catch(error => {
                        this.isAttachmentLoading = false;
                        this.errorTitle = 'Failed to Open Attachment';

                        if(error?.response?.data?.data) {
                          this.errorMsg = "Error: \n\n" + error.response.data.data;
                        } else {
                            this.errorMsg = '';
                        }

                        this.onRequestError(this.errorTitle, this.errorMsg);
                    });
            },
            populateExternal() {
                this.editedCondition.externalDescription = this.editedCondition.internalDescription;
                this.checkForConditionChanges();
            },
            populateInternal() {
                this.editedCondition.internalDescription = this.editedCondition.externalDescription;
                this.checkForConditionChanges();
            },
            processSelectedCondition() {
                this.editedCondition = {
                    id: this.condition.id,
                    internalDescription: this.condition.internalDescription ? this.condition.internalDescription : '',
                    externalDescription: this.condition.externalDescription ? this.condition.externalDescription : '',
                    priorTo: this.condition.priorTo ? this.condition.priorTo : '',
                    title: this.condition.title,
                    printDefinitions: this.condition.printDefinitions ? cloneDeep(this.condition.printDefinitions).sort() : []
                }

                this.uneditedCondition = Object.assign({}, this.editedCondition);
                
                this.conditionAttachments = this.condition.attachments.map(att => ({...att}));

                if (this.conditionAttachments.length > 0) {
                    this.conditionAttachments.forEach(att => att.selected = false);
                    this.attachmentSort = [{ dir: 'desc', field: 'createdDate' }];
                    this.conditionAttachments = orderBy(this.conditionAttachments, this.attachmentSort);

                    this.assignSelectedDocument();
                }

                this.trackingStatus = JSON.parse(JSON.stringify(this.trackingStatusModel));

                if (this.condition.tracking && Array.isArray(this.condition.tracking)) {
                    this.condition.tracking.forEach(track => {
                        let trackingStatusIndex = this.trackingStatus.findIndex(status => status.status === track.status);

                        if (trackingStatusIndex >= 0) {
                            this.trackingStatus[trackingStatusIndex].status = track.status;
                            this.trackingStatus[trackingStatusIndex].isChecked = track.isChecked;                         
                            this.trackingStatus[trackingStatusIndex].user = track.user ? track.user.entityName : '';
                            this.trackingStatus[trackingStatusIndex].statusDate = track.date ? new Date(track.date).toLocaleDateString() : '';
                        }
                    });
                }

                this.undeditedTrackingStatus = JSON.parse(JSON.stringify(this.trackingStatus));

                if (this.condition.assignedTo && this.condition.assignedTo.length > 0) {
                    this.availableDocuments = this.allDocuments.filter(doc => !this.condition.assignedTo.some(d => d.entityId === doc.id) && doc.title !== '* Junk Folder');
                    this.currentAssignedDocuments = this.allDocuments.filter(doc => this.condition.assignedTo.some(d => d.entityId === doc.id));
                } else {
                    this.availableDocuments = this.allDocuments.filter(doc => doc.title !== '* Junk Folder').map(doc => ({...doc}));
                }

                if (this.condition.printDefinitions) {
                    this.conditionPrintDefinitions.forEach(def => {
                        if (this.condition.printDefinitions.some(d => d === def.type)) {
                            def.checked = true;
                        } else {
                            def.checked = false;
                        }
                    });
                } 

                this.filteredAvailableDocuments = this.availableDocuments.map(doc => ({...doc}));

                this.checkForConditionChanges();

                this.$nextTick(() => {
                    let panelTextEl = document.querySelector('.k-panelbar-item-text');

                    if (panelTextEl) {
                        panelTextEl.innerHTML += `<span class="attachments-badge">${this.conditionAttachments.length}</span>`;
                    }
                })
            },
            saveCondition() {
                if (this.saveConditionBtnDisabled) return this.closeDialog();

                this.saveChangesWarning = false;

                if (this.conditionUpdated) {
                    return this.updateCondition();
                }

                if (this.trackingUpdated) {
                    let tracking = this.getUpdatedTracking();

                    this.$emit('updatetracking', cloneDeep(this.editedCondition), cloneDeep(tracking));
                }
            },
            showNotification(notificationMsg) {
                this.notificationMsg = notificationMsg;
                this.successNotification = true;

                setTimeout(() => {
                    this.successNotification = false;
                    this.notificationMsg = '';
                }, 4000);
            },
            toggleAssignDocumentDialog() {
                this.showDocumentsDialog = !this.showDocumentsDialog;
            },
            toggleConfirmCloseDialog() {
                this.showConfirmCloseDialog = !this.showConfirmCloseDialog;
            },
            toggleErrorDialog() {
                this.showErrorDialog = !this.showErrorDialog;

                if (!this.showErrorDialog) {
                    this.errorAlert = false;
                    this.errorMsg = '';
                }
            },
            updateCondition() {
                if (!this.trackingUpdated) {
                    this.$emit('updatecondition', cloneDeep(this.editedCondition), this.trackingUpdated);
                } else {
                    let tracking = this.getUpdatedTracking();

                    this.$emit('updatecondition', cloneDeep(this.editedCondition), this.trackingUpdated, cloneDeep(tracking));
                }
            }
        },
        components: {
            'k-dialog': Dialog,
            'k-dialog-actions': DialogActionsBar,
            'k-textarea': TextArea,
            'k-button': Button,
            'k-dropdown': DropDownList,
            'k-multiselect': MultiSelect,
            'k-checkbox': Checkbox,
            'loader': Loader,
            'k-grid': Grid,
            'k-grid-toolbar': GridToolbar,
            'k-notification': Notification,
            'k-notification-group': NotificationGroup,
            'k-fade': Fade,
            'k-panelbar': PanelBar,
            'k-input': Input,
            WebViewer
        }
    }
</script>

<style>
    @import '/src/assets/css/editConditionDialog.css';
</style>
